import { useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { Ship, Route, Mountain } from "lucide-react";
import { SectionLabel } from "./Reveal";
import { useLang } from "../i18n";
import { TUNISIA_MAINLAND, TUNISIA_ISLANDS } from "./tunisiaGeo";

const ICON = { ferry: Ship, road: Route, track: Mountain };
const GOLD = "#D4AF37";

// Projection shared with tunisiaGeo.js: x = (lon - 7.4) * 80, y = (37.6 - lat) * 97.6
// Stops in the order they are visited (index 0 = Civitavecchia, off the coast at the top)
const STOPS = [
  [235, -70], // Civitavecchia
  [222, 78],  // Tunis
  [130, 403], // Douz
  [166, 439], // Tembaine
  [178, 451], // Ksar Ghilane
  [206, 396], // Matmata
  [277, 366], // Djerba
  [222, 78],  // Tunis
  [235, -70], // Civitavecchia
];
const LABEL_OFFSET = [[14, -4], [14, 4], [-14, 6], [-10, 22], [14, 10], [14, -8], [14, 4], [14, 4], [14, -4]];
const SEGMENTS = [
  "M235 -70 C 252 -10, 262 52, 224 76",
  "M222 78 C 205 180, 240 320, 130 403",
  "M130 403 Q 140 430 166 439",
  "M166 439 Q 170 450 178 451",
  "M178 451 Q 200 430 206 396",
  "M206 396 Q 240 390 277 366",
  "M277 366 C 268 290, 258 160, 222 78",
  "M224 78 C 270 58, 258 -8, 235 -70",
];
const CITIES = [
  ["Bizerte", 197.6, 32.2], ["Hammamet", 256, 117], ["Sousse", 259, 173], ["Kairouan", 216, 187], ["Sfax", 269, 279],
  ["Gabès", 216, 363], ["Tozeur", 58.4, 359.2], ["Kebili", 142, 383], ["Médenine", 262, 416], ["Tataouine", 232, 476],
];
const N = SEGMENTS.length;
const T0 = 0.06, T1 = 0.94;
const seg = (i) => [T0 + (i * (T1 - T0)) / N, T0 + ((i + 1) * (T1 - T0)) / N];

const Segment = ({ d, i, progress, sea }) => {
  const [a, b] = seg(i);
  const pathLength = useTransform(progress, [a, b], [0, 1]);
  const opacity = useTransform(progress, [a, a + (b - a) * 0.6], [0, 1]);
  // Framer's pathLength drives stroke-dasharray, so ferry legs keep their dashes and fade in instead
  if (sea) return <motion.path d={d} fill="none" stroke={GOLD} strokeWidth="1.6" strokeLinecap="round" strokeDasharray="4 6" style={{ opacity }} />;
  return <motion.path d={d} fill="none" stroke={GOLD} strokeWidth="2.4" strokeLinecap="round" style={{ pathLength }} />;
};

const Stop = ({ i, name, progress, order }) => {
  const [x, y] = STOPS[order];
  const [dx, dy] = LABEL_OFFSET[order];
  const t = order === 0 ? T0 : seg(order - 1)[1];
  const o = useTransform(progress, [t - 0.015, t], [0, 1]);
  const sc = useTransform(progress, [t - 0.015, t], [0.4, 1]);
  const lo = useTransform(progress, [t - 0.015, t], [0.5, 1]);
  return (
    <g data-testid={`map-stop-${i}`}>
      <motion.g style={{ opacity: o, scale: sc, transformOrigin: `${x}px ${y}px` }}>
        <circle cx={x} cy={y} r="9" fill={GOLD} opacity="0.18" />
        <circle cx={x} cy={y} r="3.4" fill="#F3E5AB" />
      </motion.g>
      <circle cx={x} cy={y} r="1.6" fill={GOLD} />
      <motion.text style={{ opacity: lo }} x={x + dx} y={y + dy} fill="#F7F4EE" stroke="#0B0A08" strokeWidth="3" paintOrder="stroke" fontWeight="600"
        className="text-[17px] lg:text-[11.5px]" fontFamily="Manrope, sans-serif" letterSpacing="0.04em" textAnchor={dx < 0 ? "end" : "start"} data-testid={`map-stop-label-${i}`}>{name}</motion.text>
    </g>
  );
};

export const RouteMap = () => {
  const { t, lang } = useLang();
  const days = t.itinerary.days;
  const names = [...days.map((d) => d[0]), days[days.length - 1][1]];
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const [active, setActive] = useState(0);
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    const idx = Math.min(N - 1, Math.max(0, Math.floor(((v - T0) / (T1 - T0)) * N)));
    if (idx !== active) setActive(idx);
  });
  const barW = useTransform(scrollYProgress, [T0, T1], ["0%", "100%"]);
  const [from, to, type, text] = days[active];
  const Icon = ICON[type];

  return (
    <section id="map" ref={ref} className="relative h-[260vh] lg:h-[320vh] bg-[#0B0A08] border-t border-[#D4AF37]/15" data-testid="map-section">
      <div className="sticky top-0 h-[100svh] overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none opacity-50" />
        <div className="relative h-full max-w-[1440px] mx-auto px-5 md:px-10 pt-20 pb-4 md:pt-24 md:pb-10 grid grid-rows-[auto_1fr_auto] lg:grid-rows-1 lg:grid-cols-12 gap-3 lg:gap-12">
          <div className="lg:col-span-5 lg:flex lg:flex-col lg:justify-between min-h-0 contents lg:block">
            <div className="row-start-1">
              <SectionLabel index="01·b">{t.map.label}</SectionLabel>
              <h2 key={lang} className="mt-3 md:mt-8 font-display text-2xl sm:text-4xl lg:text-6xl leading-[1.05] text-[#F7F4EE]">{t.map.title}</h2>
            </div>
            <div className="row-start-3 lg:mt-0">
              <div className="relative h-px bg-[#D4AF37]/20 overflow-hidden">
                <motion.div style={{ width: barW }} className="absolute inset-y-0 left-0 bg-[#D4AF37]" data-testid="map-progress" />
              </div>
              <AnimatePresence mode="wait">
                <motion.div key={active} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }} className="pt-4 md:pt-8" data-testid="map-active-day">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#D4AF37]">{t.map.day} {String(active + 1).padStart(2, "0")} / {String(N).padStart(2, "0")}</span>
                    <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#B5AC9C]"><Icon size={13} strokeWidth={1.5} /> {t.itinerary.types[type]}</span>
                  </div>
                  <p className="mt-2 md:mt-3 font-display text-2xl sm:text-3xl text-[#F7F4EE]">{from} <span className="text-[#D4AF37]">→</span> {to}</p>
                  <p className="mt-2 md:mt-3 text-sm text-[#B5AC9C] leading-relaxed max-w-md line-clamp-2 sm:line-clamp-3 lg:line-clamp-none">{text}</p>
                </motion.div>
              </AnimatePresence>
              <p className="mt-5 hidden lg:flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[#E6C594]/80">{t.map.hint}<span className="scroll-line" /></p>
            </div>
          </div>

          <div className="row-start-2 lg:row-start-auto lg:col-span-7 min-h-0 flex items-center justify-center">
            <svg viewBox="-20 -100 400 840" className="hidden lg:block h-full max-h-full w-auto lg:scale-[1.08] origin-center" data-testid="map-svg" aria-label="Mappa della Tunisia con le tappe">
              <MapArt t={t} progress={scrollYProgress} names={names} />
            </svg>
            <svg viewBox="-20 -95 400 585" className="lg:hidden h-full w-auto max-w-full" data-testid="map-svg-mobile" aria-label="Mappa della Tunisia con le tappe">
              <MapArt t={t} progress={scrollYProgress} names={names} mobile />
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
};

const MapArt = ({ t, progress, names, mobile }) => {
  const k = mobile ? "m" : "d"; // unique def ids: both SVGs live in the DOM (one hidden per breakpoint)
  return (
  <>
              <defs>
                <pattern id={`dunes-${k}`} width="10" height="10" patternUnits="userSpaceOnUse">
                  <circle cx="2" cy="2" r="0.9" fill={GOLD} opacity="0.35" />
                </pattern>
                <radialGradient id={`landGlow-${k}`} cx="50%" cy="40%" r="70%">
                  <stop offset="0" stopColor="#1C1813" />
                  <stop offset="1" stopColor="#120F0B" />
                </radialGradient>
                <clipPath id={`landClip-${k}`}><path d={TUNISIA_MAINLAND} /></clipPath>
              </defs>
              <path d={TUNISIA_MAINLAND} fill={`url(#landGlow-${k})`} stroke={GOLD} strokeOpacity="0.6" strokeWidth="1" strokeLinejoin="round" data-testid="map-tunisia-outline" />
              {TUNISIA_ISLANDS.map((d, i) => <path key={i} d={d} fill={`url(#landGlow-${k})`} stroke={GOLD} strokeOpacity="0.6" strokeWidth="0.8" strokeLinejoin="round" />)}
              <path d="M40 440 L120 425 L220 468 L310 470 L340 560 L240 740 L120 740 L70 560 Z" fill={`url(#dunes-${k})`} opacity="0.9" clipPath={`url(#landClip-${k})`} />
              <ellipse cx="80" cy="381" rx="46" ry="11" fill="#F7F4EE" opacity="0.08" stroke="#F7F4EE" strokeOpacity="0.25" strokeWidth="0.6" />
              <text x="80" y="384" fill="#F7F4EE" fillOpacity="0.5" fontSize="7" fontFamily="JetBrains Mono, monospace" textAnchor="middle" letterSpacing="0.2em">CHOTT EL JERID</text>
              <text x="200" y={mobile ? 486 : 600} fill={GOLD} fillOpacity="0.55" fontSize={mobile ? 10 : 8} fontFamily="JetBrains Mono, monospace" textAnchor="middle" letterSpacing="0.3em">GRAND ERG ORIENTAL</text>
              <text x="40" y="-20" fill="#F7F4EE" fillOpacity="0.35" fontSize={mobile ? 10 : 8} fontFamily="JetBrains Mono, monospace" letterSpacing="0.3em">MAR MEDITERRANEO</text>
              <text x="10" y="300" fill="#F7F4EE" fillOpacity="0.3" fontSize={mobile ? 9 : 7} fontFamily="JetBrains Mono, monospace" letterSpacing="0.3em" transform="rotate(-78 10 300)">ALGERIA</text>
              {!mobile && <text x="318" y="520" fill="#F7F4EE" fillOpacity="0.3" fontSize="7" fontFamily="JetBrains Mono, monospace" letterSpacing="0.3em" transform="rotate(62 318 520)">LIBIA</text>}
              {CITIES.map(([n, x, y]) => (
                <g key={n} opacity="0.45">
                  <circle cx={x} cy={y} r="1.6" fill="#F7F4EE" />
                  <text x={x + 6} y={y + 3} fill="#F7F4EE" fontSize={mobile ? 11 : 8} fontFamily="Manrope, sans-serif">{n}</text>
                </g>
              ))}
              {SEGMENTS.map((d, i) => <Segment key={i} d={d} i={i} progress={progress} sea={i === 0 || i === N - 1} />)}
              {[0, 1, 2, 3, 4, 5, 6].map((i) => <Stop key={i} i={i} order={i} name={names[i]} progress={progress} />)}
              <g transform="translate(22 -78)" fill="none" stroke="#F7F4EE" strokeOpacity="0.5" strokeWidth="1">
                <path d="M0 8 h26 l-4 5 h-18z M6 8 v-6 h10 v6" />
              </g>
              {!mobile && (
                <g fontFamily="JetBrains Mono, monospace" fontSize="7" letterSpacing="0.15em" fill="#B5AC9C" transform="translate(240 690)">
                  <line x1="0" y1="0" x2="22" y2="0" stroke={GOLD} strokeWidth="1.6" strokeDasharray="4 6" /><text x="30" y="3">{t.map.sea.toUpperCase()}</text>
                  <line x1="0" y1="16" x2="22" y2="16" stroke={GOLD} strokeWidth="2.4" /><text x="30" y="19">{t.map.land.toUpperCase()}</text>
                </g>
              )}
  </>
  );
};
